// Análise das conversas de WhatsApp pela IA (#6313).
//
// Um relatório por WhatsApp por dia: números do dia (calculados pelo sistema),
// o texto da IA (resumo, pontos fortes, o que melhorar, insights) e a lista de
// conversas com chance de fechamento, próximo passo e alertas de política.
//
// Quem vê: adm e gerente veem todos; a atendente vê só o dela (RLS). Gerar ou
// refazer um dia é só para adm e gerente.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle, ChevronDown, ChevronLeft, ChevronRight, ExternalLink, Flame, Lightbulb, Loader2,
  MessageSquare, RefreshCw, ShieldAlert, Sparkles, ThumbsUp, TrendingUp, Users,
} from 'lucide-react';
import { useAuth } from '../../../src/contexts/AuthContext';
import NegotiationDetailModal from '../components/NegotiationDetails/NegotiationDetailModal';
import { oportunidadesDoDia, type Oportunidade } from '../../../supabase/functions/_shared/analiseConversas.ts';
import {
  carregarDia, gerarDia, listarRelatorios,
  type AnaliseConversaLinha, type RelatorioDia,
} from './apiAnaliseConversas';

const ETAPA: Record<string, string> = {
  primeiro_contato: 'Primeiro contato',
  qualificacao: 'Qualificação',
  apresentacao: 'Apresentação',
  simulacao_proposta: 'Simulação / proposta',
  objecoes_negociacao: 'Objeções / negociação',
  documentos_assinatura: 'Documentos / assinatura',
  fechado: 'Fechado',
  perdido: 'Perdido',
  pos_venda_suporte: 'Pós-venda / suporte',
};

const BANT: Record<string, string> = { identificado: 'Sim', parcial: 'Em parte', nao_identificado: 'Não' };

/** US$ por milhão de tokens (entrada, saída), para a estimativa de custo. */
const PRECO: Record<string, [number, number]> = { 'gpt-4.1-mini': [0.4, 1.6], 'gpt-4o-mini': [0.15, 0.6] };

const hoje = () => new Date(Date.now() - 3 * 3600 * 1000).toISOString().slice(0, 10);
const somarDias = (dia: string, n: number) => {
  const d = new Date(`${dia}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const diaBr = (dia: string) => dia.split('-').reverse().join('/');
const min = (v: number | null | undefined) => (v == null ? '—' : v < 60 ? `${Math.round(v)} min` : `${(v / 60).toFixed(1).replace('.', ',')} h`);

function corChance(p: number | null): string {
  if (p == null) return 'bg-gray-300 dark:bg-slate-600';
  if (p >= 60) return 'bg-emerald-500';
  if (p >= 30) return 'bg-amber-500';
  return 'bg-rose-400';
}

const TEMP: Record<string, string> = {
  quente: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
  morna: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  fria: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',
};

const Cartao: React.FC<{ titulo: string; icone?: React.ReactNode; children: React.ReactNode; className?: string }> = ({ titulo, icone, children, className = '' }) => (
  <section className={`rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900 ${className}`}>
    <h2 className="mb-3 flex items-center gap-2 text-sm font-bold text-gray-900 dark:text-white">{icone}{titulo}</h2>
    {children}
  </section>
);

const Numero: React.FC<{ rotulo: string; valor: React.ReactNode; detalhe?: React.ReactNode }> = ({ rotulo, valor, detalhe }) => (
  <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm dark:border-slate-700 dark:bg-slate-900">
    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">{rotulo}</p>
    <p className="mt-1 text-xl font-bold text-gray-900 dark:text-white">{valor}</p>
    {detalhe && <p className="mt-0.5 text-[11px] text-gray-500 dark:text-slate-400">{detalhe}</p>}
  </div>
);

const Chance: React.FC<{ p: number | null }> = ({ p }) => (
  <div className="flex items-center gap-2">
    <div className="h-1.5 w-16 overflow-hidden rounded-full bg-gray-100 dark:bg-slate-800">
      <div className={`h-full ${corChance(p)}`} style={{ width: `${p ?? 0}%` }} />
    </div>
    <span className="w-9 text-right text-xs font-semibold tabular-nums text-gray-700 dark:text-slate-200">{p == null ? '—' : `${p}%`}</span>
  </div>
);

const AnaliseConversasView: React.FC = () => {
  const { profile } = useAuth();
  const ehGestao = profile?.role === 'adm' || profile?.role === 'gerente';
  const [params, setParams] = useSearchParams();

  const [lista, setLista] = useState<Awaited<ReturnType<typeof listarRelatorios>>>([]);
  const [conexao, setConexao] = useState<string | null>(params.get('conexao'));
  const [dia, setDia] = useState<string>(params.get('dia') || '');
  const [relatorio, setRelatorio] = useState<RelatorioDia | null>(null);
  const [conversas, setConversas] = useState<AnaliseConversaLinha[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [gerando, setGerando] = useState<string | null>(null);
  const [aberta, setAberta] = useState<string | null>(null);
  const [negociacaoAberta, setNegociacaoAberta] = useState<number | null>(null);

  // ── Lista de dias analisados; escolhe o mais recente quando a URL não diz ──
  const carregarLista = useCallback(async () => {
    const l = await listarRelatorios();
    setLista(l);
    return l;
  }, []);

  useEffect(() => {
    carregarLista()
      .then((l) => {
        setConexao((c) => c ?? l[0]?.connection_id ?? null);
        setDia((d) => d || l[0]?.dia || hoje());
      })
      .catch((e) => setErro(e.message ?? 'Não foi possível carregar.'));
  }, [carregarLista]);

  const conexoes = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of lista) if (!m.has(r.connection_id)) m.set(r.connection_id, `${r.conexao_nome}`);
    return [...m.entries()];
  }, [lista]);

  const diasDaConexao = useMemo(() => lista.filter((r) => r.connection_id === conexao).slice(0, 14), [lista, conexao]);

  // ── O dia escolhido ──
  const carregar = useCallback(async () => {
    if (!dia) return;
    setCarregando(true);
    setErro(null);
    try {
      if (!conexao) {
        setRelatorio(null);
        setConversas([]);
        return;
      }
      const r = await carregarDia(conexao, dia);
      setRelatorio(r.relatorio);
      setConversas(r.conversas);
    } catch (e) {
      setErro((e as Error).message ?? 'Não foi possível carregar.');
    } finally {
      setCarregando(false);
    }
  }, [conexao, dia]);

  useEffect(() => {
    carregar();
    if (dia) {
      const p: Record<string, string> = { dia };
      if (conexao) p.conexao = conexao;
      setParams(p, { replace: true });
    }
  }, [carregar]); // eslint-disable-line react-hooks/exhaustive-deps

  const gerar = async (refazer: boolean) => {
    setGerando('Lendo as conversas…');
    setErro(null);
    try {
      await gerarDia(dia, {
        refazer,
        connectionId: conexao,
        aoProgredir: (pendentes) => setGerando(pendentes ? `Faltam ${pendentes} conversas…` : 'Escrevendo o relatório…'),
      });
      const l = await carregarLista();
      if (!conexao && l[0]) setConexao(l[0].connection_id);
      await carregar();
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setGerando(null);
    }
  };

  const ops = useMemo(() => oportunidadesDoDia(conversas), [conversas]);
  const alertas = useMemo(
    () => conversas.flatMap((c) => (c.alertas_politica ?? []).map((a) => ({ ...a, conversa: c }))),
    [conversas],
  );
  const m = relatorio?.metricas;
  const custo = useMemo(() => {
    if (!relatorio?.modelo || !relatorio.tokens_entrada) return null;
    const [e, s] = PRECO[relatorio.modelo] ?? PRECO['gpt-4.1-mini'];
    return ((relatorio.tokens_entrada ?? 0) * e + (relatorio.tokens_saida ?? 0) * s) / 1_000_000;
  }, [relatorio]);

  const linkContato = (c: { contato_nome: string | null; negociacao_id: number | null }) =>
    c.negociacao_id ? (
      <button onClick={() => setNegociacaoAberta(c.negociacao_id)} className="inline-flex items-center gap-1 font-semibold text-gray-900 hover:text-primary dark:text-white">
        {c.contato_nome?.trim() || 'Contato sem nome'}
        <ExternalLink size={11} className="opacity-60" />
      </button>
    ) : (
      <span className="font-semibold text-gray-900 dark:text-white">{c.contato_nome?.trim() || 'Contato sem nome'}</span>
    );

  const ListaOportunidades: React.FC<{ itens: Oportunidade[]; vazio: string }> = ({ itens, vazio }) =>
    itens.length ? (
      <ul className="divide-y divide-gray-100 dark:divide-slate-800">
        {itens.map((o) => (
          <li key={o.conversation_id} className="py-2 text-xs">
            <div className="flex items-center justify-between gap-2">
              {linkContato(o)}
              <Chance p={o.probabilidade} />
            </div>
            <p className="mt-0.5 text-gray-500 dark:text-slate-400">{o.motivo}</p>
            {o.proximo_passo && <p className="mt-1 text-gray-700 dark:text-slate-200"><span className="font-semibold">Próximo passo:</span> {o.proximo_passo}</p>}
          </li>
        ))}
      </ul>
    ) : <p className="text-xs text-gray-400 dark:text-slate-500">{vazio}</p>;

  return (
    <div className="space-y-4 px-2 sm:px-0">
      {/* Cabeçalho */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-primary/10 p-2.5 text-primary dark:bg-primary/20">
            <Sparkles size={22} />
          </div>
          <div>
            <h1 className="text-lg font-bold leading-tight text-gray-900 dark:text-white">Análise das conversas</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">A IA lê as conversas de WhatsApp todo dia às 19h e aponta o que fazer amanhã</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {conexoes.length > 1 && (
            <select
              value={conexao ?? ''}
              onChange={(e) => setConexao(e.target.value || null)}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
            >
              {conexoes.map(([id, nome]) => <option key={id} value={id}>{nome}</option>)}
            </select>
          )}
          <div className="flex items-center rounded-lg border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-900">
            <button onClick={() => setDia((d) => somarDias(d, -1))} className="p-2 text-gray-500 hover:text-primary" title="Dia anterior"><ChevronLeft size={16} /></button>
            <input
              type="date"
              value={dia}
              max={hoje()}
              onChange={(e) => e.target.value && setDia(e.target.value)}
              className="bg-transparent px-1 py-1.5 text-sm text-gray-800 outline-none dark:text-slate-100"
            />
            <button onClick={() => setDia((d) => (d < hoje() ? somarDias(d, 1) : d))} className="p-2 text-gray-500 hover:text-primary" title="Próximo dia"><ChevronRight size={16} /></button>
          </div>
          {ehGestao && (
            <button
              onClick={() => gerar(!!relatorio)}
              disabled={!!gerando}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary/90 disabled:opacity-60"
              title={relatorio ? 'Analisa de novo todas as conversas deste dia' : 'Analisa as conversas deste dia agora'}
            >
              {gerando ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
              {gerando ?? (relatorio ? 'Refazer o dia' : 'Gerar este dia')}
            </button>
          )}
        </div>
      </div>

      {diasDaConexao.length > 1 && (
        <div className="flex flex-wrap gap-1.5">
          {diasDaConexao.map((r) => (
            <button
              key={r.id}
              onClick={() => setDia(r.dia)}
              className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${
                r.dia === dia
                  ? 'border-primary/30 bg-primary/10 text-primary dark:bg-primary/25 dark:text-[#B4C77A]'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-primary/30 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300'
              }`}
            >
              {diaBr(r.dia).slice(0, 5)} · {r.nota ?? '—'}/10
            </button>
          ))}
        </div>
      )}

      {erro && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300">
          <AlertTriangle size={15} /> {erro}
        </div>
      )}

      {carregando ? (
        <div className="flex min-h-[240px] items-center justify-center"><Loader2 size={22} className="animate-spin text-gray-300" /></div>
      ) : !relatorio ? (
        <div className="flex min-h-[240px] flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 bg-white px-6 text-center dark:border-slate-700 dark:bg-slate-900">
          <MessageSquare size={26} className="text-gray-300 dark:text-slate-600" />
          <p className="text-sm font-semibold text-gray-700 dark:text-slate-200">Sem relatório para {dia ? diaBr(dia) : 'este dia'}</p>
          <p className="max-w-md text-xs text-gray-500 dark:text-slate-400">
            {conversas.length
              ? `${conversas.length} conversas já analisadas; o relatório sai quando todas terminarem.`
              : 'Nenhuma conversa analisada neste dia. Em dia sem conversa no WhatsApp, ou com o WhatsApp desconectado, não há relatório.'}
            {ehGestao && ' Use "Gerar este dia" para analisar agora.'}
          </p>
        </div>
      ) : (
        <>
          {/* Números do dia — calculados pelo sistema, não pela IA */}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            <Numero rotulo="Nota do dia" valor={`${relatorio.nota ?? '—'}/10`} detalhe={relatorio.atendente_nome ?? relatorio.conexao_nome} />
            <Numero rotulo="Conversas" valor={m?.conversas ?? 0} detalhe={`${m?.conversas_comerciais ?? 0} de venda`} />
            <Numero rotulo="Mensagens" valor={`${m?.mensagens_recebidas ?? 0} / ${m?.mensagens_enviadas ?? 0}`} detalhe="recebidas / enviadas" />
            <Numero rotulo="Tempo até responder" valor={min(m?.tempo_resposta_mediana_min)} detalhe={m?.tempo_resposta_p90_min != null ? `90% em até ${min(m.tempo_resposta_p90_min)} · 8h às 18h` : 'sem turnos no horário comercial'} />
            <Numero rotulo="Sem resposta" valor={m?.clientes_sem_resposta ?? 0} detalhe="clientes esperando no fim do dia" />
            <Numero rotulo="Chance média" valor={m?.probabilidade_media != null ? `${m.probabilidade_media}%` : '—'} detalhe={`${m?.quentes ?? 0} quentes · ${m?.mornas ?? 0} mornas · ${m?.frias ?? 0} frias`} />
          </div>

          <Cartao titulo="Resumo do dia" icone={<Sparkles size={15} className="text-primary" />}>
            <p className="whitespace-pre-line text-sm leading-relaxed text-gray-700 dark:text-slate-200">{relatorio.resumo}</p>
          </Cartao>

          <div className="grid gap-4 lg:grid-cols-2">
            <Cartao titulo="Pontos fortes" icone={<ThumbsUp size={15} className="text-emerald-600" />}>
              {relatorio.pontos_fortes.length ? (
                <ul className="space-y-2 text-sm text-gray-700 dark:text-slate-200">
                  {relatorio.pontos_fortes.map((p, i) => <li key={i} className="flex gap-2"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />{p}</li>)}
                </ul>
              ) : <p className="text-xs text-gray-400">Nada em destaque.</p>}
            </Cartao>
            <Cartao titulo="O que melhorar" icone={<TrendingUp size={15} className="text-amber-600" />}>
              {relatorio.melhorias.length ? (
                <ol className="space-y-3">
                  {relatorio.melhorias.map((mel, i) => (
                    <li key={i} className="text-sm">
                      <p className="font-semibold text-gray-900 dark:text-white">{i + 1}. {mel.titulo}</p>
                      <p className="mt-0.5 text-gray-700 dark:text-slate-200">{mel.como_fazer}</p>
                      {mel.exemplo && <p className="mt-1 rounded-lg border-l-2 border-primary bg-primary/5 px-3 py-1.5 text-xs italic text-gray-700 dark:bg-primary/10 dark:text-slate-200">“{mel.exemplo}”</p>}
                    </li>
                  ))}
                </ol>
              ) : <p className="text-xs text-gray-400">Nada a apontar.</p>}
            </Cartao>
          </div>

          {alertas.length > 0 && (
            <Cartao titulo={`Promessas fora da política (${alertas.length})`} icone={<ShieldAlert size={15} className="text-rose-600" />} className="border-rose-200 dark:border-rose-900/60">
              <ul className="space-y-3">
                {alertas.map((a, i) => (
                  <li key={i} className="rounded-lg bg-rose-50/60 p-3 text-xs dark:bg-rose-900/10">
                    <div className="flex flex-wrap items-center gap-2">
                      {linkContato(a.conversa)}
                      <span className="text-gray-400">·</span>
                      <span className="font-semibold text-rose-700 dark:text-rose-300">{a.regra}</span>
                      <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${a.gravidade === 'alta' ? 'bg-rose-600 text-white' : a.gravidade === 'media' ? 'bg-amber-200 text-amber-800' : 'bg-gray-200 text-gray-700'}`}>{a.gravidade}</span>
                    </div>
                    <p className="mt-1.5 italic text-gray-800 dark:text-slate-100">“{a.trecho}”</p>
                    <p className="mt-1 text-gray-600 dark:text-slate-300">{a.explicacao}</p>
                  </li>
                ))}
              </ul>
            </Cartao>
          )}

          <div className="grid gap-4 lg:grid-cols-2">
            <Cartao titulo={`Mais perto de fechar (${ops.quentes.length})`} icone={<Flame size={15} className="text-rose-500" />}>
              <ListaOportunidades itens={ops.quentes} vazio="Nenhuma conversa com chance de 60% ou mais." />
            </Cartao>
            <Cartao titulo={`Em risco (${ops.em_risco.length})`} icone={<AlertTriangle size={15} className="text-amber-500" />}>
              <ListaOportunidades itens={ops.em_risco} vazio="Nenhuma venda escapando hoje." />
            </Cartao>
          </div>

          {(relatorio.insights.length > 0 || relatorio.padroes_objecao.length > 0 || relatorio.recomendacoes_gestao.length > 0) && (
            <div className="grid gap-4 lg:grid-cols-3">
              {relatorio.insights.length > 0 && (
                <Cartao titulo="Insights" icone={<Lightbulb size={15} className="text-amber-500" />}>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-slate-200">{relatorio.insights.map((x, i) => <li key={i}>{x}</li>)}</ul>
                </Cartao>
              )}
              {relatorio.padroes_objecao.length > 0 && (
                <Cartao titulo="Objeções que se repetiram" icone={<MessageSquare size={15} className="text-sky-500" />}>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-slate-200">{relatorio.padroes_objecao.map((x, i) => <li key={i}>{x}</li>)}</ul>
                </Cartao>
              )}
              {relatorio.recomendacoes_gestao.length > 0 && (
                <Cartao titulo="Para a gestão" icone={<Users size={15} className="text-indigo-500" />}>
                  <ul className="space-y-2 text-sm text-gray-700 dark:text-slate-200">{relatorio.recomendacoes_gestao.map((x, i) => <li key={i}>{x}</li>)}</ul>
                </Cartao>
              )}
            </div>
          )}

          {/* Todas as conversas */}
          <Cartao titulo={`Conversas do dia (${conversas.length})`} icone={<MessageSquare size={15} className="text-primary" />}>
            <div className="divide-y divide-gray-100 dark:divide-slate-800">
              {conversas.map((c) => {
                const av: any = c.avaliacao ?? {};
                const abertaAqui = aberta === c.id;
                return (
                  <div key={c.id} className="py-2">
                    <button onClick={() => setAberta(abertaAqui ? null : c.id)} className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 text-left text-xs">
                      <ChevronDown size={14} className={`shrink-0 text-gray-400 transition-transform ${abertaAqui ? 'rotate-180' : ''}`} />
                      <span className="min-w-[140px] flex-1 font-semibold text-gray-900 dark:text-white">{c.contato_nome?.trim() || 'Contato sem nome'}</span>
                      <span className="text-gray-500 dark:text-slate-400">{c.comercial ? ETAPA[c.etapa ?? ''] ?? c.etapa : 'Não é venda'}</span>
                      {c.temperatura && <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${TEMP[c.temperatura] ?? ''}`}>{c.temperatura}</span>}
                      {c.cliente_sem_resposta && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">sem resposta</span>}
                      {(c.alertas_politica?.length ?? 0) > 0 && <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-700 dark:bg-rose-900/30 dark:text-rose-300">alerta</span>}
                      <Chance p={c.probabilidade} />
                    </button>
                    {abertaAqui && (
                      <div className="mt-2 space-y-2 rounded-lg bg-gray-50 p-3 text-xs text-gray-700 dark:bg-slate-800/60 dark:text-slate-200">
                        {c.resumo && <p>{c.resumo}</p>}
                        {c.justificativa && <p className="text-gray-500 dark:text-slate-400"><span className="font-semibold">Por que essa chance:</span> {c.justificativa}</p>}
                        {c.proximo_passo && <p><span className="font-semibold">Próximo passo:</span> {c.proximo_passo}</p>}
                        {av.spin && (
                          <div className="flex flex-wrap gap-1.5 pt-1">
                            {[['SPIN', av.spin], ['Objeções', av.objecoes], ['Fechamento', av.fechamento], ['Clareza', av.clareza]].map(([rotulo, n]: any) => (
                              <span key={rotulo} title={n?.comentario} className="rounded-full bg-white px-2 py-0.5 font-semibold ring-1 ring-gray-200 dark:bg-slate-900 dark:ring-slate-700">{rotulo} {n?.nota ?? '—'}/10</span>
                            ))}
                            {av.bant && (
                              <span className="rounded-full bg-white px-2 py-0.5 ring-1 ring-gray-200 dark:bg-slate-900 dark:ring-slate-700">
                                BANT: conta {BANT[av.bant.orcamento]} · decisor {BANT[av.bant.autoridade]} · necessidade {BANT[av.bant.necessidade]} · prazo {BANT[av.bant.prazo]}
                              </span>
                            )}
                          </div>
                        )}
                        {c.objecoes?.length > 0 && <p><span className="font-semibold">Objeções:</span> {c.objecoes.map((o) => `${o.objecao}${o.tratada ? ' (tratada)' : ' (não tratada)'}`).join('; ')}</p>}
                        {c.pontos_fortes?.length > 0 && <p><span className="font-semibold">Bom:</span> {c.pontos_fortes.join(' · ')}</p>}
                        {c.melhorias?.length > 0 && <p><span className="font-semibold">Melhorar:</span> {c.melhorias.join(' · ')}</p>}
                        {c.negociacao_id && (
                          <button onClick={() => setNegociacaoAberta(c.negociacao_id)} className="inline-flex items-center gap-1 font-semibold text-primary hover:underline">
                            Abrir negociação #{c.negociacao_id} <ExternalLink size={11} />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Cartao>

          <p className="text-[11px] text-gray-400 dark:text-slate-500">
            Análise feita por IA: confira o trecho antes de cobrar alguém. Números do topo calculados pelo sistema.
            {ehGestao && custo != null && ` Custo estimado do dia: US$ ${custo.toFixed(3).replace('.', ',')} (${relatorio.modelo}).`}
          </p>
        </>
      )}

      <NegotiationDetailModal
        negotiationId={negociacaoAberta}
        isOpen={negociacaoAberta !== null}
        onClose={() => setNegociacaoAberta(null)}
        onUpdate={() => {}}
      />
    </div>
  );
};

export default AnaliseConversasView;
